<template>
  <section class="-card-view">
    <div class="card-content">
      <a :id="`routine-${routine.id}`" :href="detailViewUrl">
        <div v-if="isDraft" class="draft-item">{{ routine.status }}</div>
        <div class="content-group -content-asset">
          <div class="group responsive-media -has-cover-info">
            <card-src-set-image
              type="card-640"
              :img-prefix="imageUrlPrefix"
              :image="routine.image"
              :title="routine.title"
            />
            <!-- image icon -->
            <image-icon
              :image-icon-set="routine.image_icon_set"
              :image-icon="routine.image_icon"
              :title="routine.title"
              sub-title="Routine"
            />
          </div>
          <plan-card-icon-bar
            :is-exclusive="!!routine.is_exclusive"
            :entity-type="entityType"
            :entity-id="routine.id"
          />
        </div>
      </a>

      <div class="content-group -content-summary">
        <div class="group">
          <div class="summary-group">
            <a :href="detailViewUrl">
              <h1 class="content-title">
                {{ routine.title }}
                <span v-if="routine.subtitle" class="sub-title">
                  <br />{{ routine.subtitle }}
                </span>
              </h1>
              <div class="primary-detail">
                {{ routine.minutes_avg }} Min/Day &bull; {{ weeksMessaging }}
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- action buttons -->
    <div class="card-actions">
      <div class="action-options">
        <div class="schedule-action">
          <calendar-button
            :entity-id="routine.id"
            :entity-type="entityType"
            :entity-title="routine.title"
            :entity-category="routine.brand.name.toLowerCase()"
            :image-url-prefix="imageUrlPrefix"
            :image="routine.image"
            :is-schedulable="isLive"
          ></calendar-button>
        </div>

        <content-actions-button
          :entity-id="routine.id"
          :entity-type="entityType"
          :entity-category="routine.brand.name.toLowerCase()"
          :entity-title="routine.title"
          :edit-button="true"
          :view-scheduled-button="!isDraft"
          :plan-copy-button="isCopyable"
          data-tour="workout-actions"
        />
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'
import { useUserAuthStore } from '@/stores/user-auth'
import CardSrcSetImage from '@/components/images/card-src-set-image.vue'
import ImageIcon from '@/components/images/image-icon.vue'
import PlanCardIconBar from '@/components/plans/plan-card-icon-bar.vue'

/** PROPS **/
const props = defineProps({
  routine: {
    type: Object,
    required: true,
  },
})

/** STATE **/
// pinia stores
const userAuthStore = useUserAuthStore()

/** COMPUTED **/
const isDraft = computed(() => props.routine.status === 'Draft')
const isLive = computed(() => props.routine.status === 'Live')
const entityType = computed(() => 'plan')

const scheduleEditUrl = computed(() => `/my/routines/${props.routine.id}/edit-schedule`)

const detailViewUrl = computed(() => {
  if (isDraft.value) {
    return scheduleEditUrl.value
  }

  return `/my/routines/${props.routine.id}`
})

const weeksMessaging = computed(() => {
  return props.routine.weeks > 1 ? `${props.routine.weeks} Weeks` : `${props.routine.weeks} Week`
})

const imageUrlPrefix = computed(() => {
  const siteAssetsPrefix = userAuthStore.cdn.siteAssetsPrefix || '/siteassets'
  return `${siteAssetsPrefix}/${props.routine.img_dir}`
})

// only enabled for workout programs currently
const isCopyable = computed(() => {
  // free users do not have access
  if (!userAuthStore.features.PLAN_COPY) {
    return false
  }

  // only allow copy of live routines
  return isLive.value
})
</script>
